<template>
  <section class="collection-page">
    <section
      class="container grid pb-10 text-center collection-page__header-section md:grid-cols-2 md:grid-rows-1 md:text-left"
    >
      <div>
        <h2 class="heading">{{ page.introTitle }}</h2>
        <h3 class="heading--sm"><sanity-blocks :blocks="page.introText" /></h3>
      </div>
      <sanity-media class="py-4 px-x-gutter-sm" :image="introMedia.image" />
    </section>

    <section
      v-for="section in filteredSections"
      :key="section._key"
      :style="getSectionStyle(section)"
      class="collection-page__section"
    >
      <marquee-background
        v-if="getSectionBackgroundImages(section).length >= 1"
        :background-images="getSectionBackgroundImages(section)"
      />
      <section
        v-for="(productCategoryItem, index) in section.productCategories"
        :key="productCategoryItem._key"
        class="pt-4 pb-8 collection-page__section-area md:pt-0 md:pb-0"
        :class="{
          'collection-page__section-area--using-gallery': !isUsingProductGrid
        }"
      >
        <product-grid
          v-if="isUsingProductGrid"
          collapsible
          :hide-top-marquee="index !== 0"
          hide-bottom-marquee
          :button-background-color="getSectionButtonBackground(section)"
          :clouds-disabled="getSectionCloudsDisabled(section)"
          :col-count="4"
          :marquees="productCategoryItem.productCategory.marquees"
          :products="productCategoryItem.productCategory.products"
          :show-more-text="productCategoryItem.showMoreText"
        >
          <template #header>
            <section class="container py-8 collection-page__section-header">
              <h2 class="heading">{{ productCategoryItem.header }}</h2>
              <h3 class="heading--sm">
                <sanity-blocks :blocks="productCategoryItem.subheader" />
              </h3>
            </section>
          </template>
        </product-grid>

        <div v-else-if="bp">
          <sky v-if="!getSectionCloudsDisabled(section)" :columns="2" />
          <section class="container py-8 collection-page__section-header">
            <h2 class="heading">{{ productCategoryItem.header }}</h2>
            <h3 class="heading--sm">
              <sanity-blocks :blocks="productCategoryItem.subheader" />
            </h3>
          </section>
          <product-gallery
            :products="productCategoryItem.productCategory.products"
            :loop="true"
            :slides-per-view="2"
            :set-active-colors="false"
            square
          />
        </div>
      </section>
    </section>
  </section>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import Color from 'color'
import sanityQuery from '../../../sanity/queries/collection'
import page from '~/mixins/page'
import productGrid from '~/components/shop/products/product-grid'
import productGallery from '~/components/shop/products/product-gallery'
import marqueeBackground from '~/components/marquee-background'
import { colors } from '~/recess.json'
import getCBDFilter from '~/util/get-cbd-filter'

export default {
  name: 'CollectionPage',

  components: {
    productGallery,
    productGrid,
    marqueeBackground
  },

  mixins: [page],

  async asyncData({ route, params, error, query, $sanity, $sentry, store }) {
    const slug = params.page
    const previewId = query.preview
    try {
      const page = await $sanity.fetch(sanityQuery(slug, previewId))
      if (!page || Object.keys(page).length === 0) {
        throw new Error('Page not found')
      }
      return { page }
    } catch (e) {
      $sentry.captureException(e)
      return error({
        statusCode: 404,
        message: e.message
      })
    }
  },

  data() {
    return {
      page: undefined,
      nonCBDSite: process.env.nonCBDSite,
      isCBDSafe: getCBDFilter(process.env.nonCBDSite)
    }
  },

  computed: {
    ...mapGetters('screen', {
      bp: 'breakpoint'
    }),
    filteredSections() {
      return this.page.sections
        .map(section => {
          section.productCategories = section.productCategories.filter(
            category => this.isCBDSafe(category.productCategory)
          )

          return section
        })
        .filter(section => {
          return section.productCategories.length > 0
        })
    },
    backgroundColors() {
      return [this.page.introBackgroundColor.hex] ?? [colors.lavender]
    },
    colors() {
      return {
        backgroundColors: this.backgroundColors
      }
    },
    isUsingProductGrid() {
      return this.bp?.md
    },
    introMedia() {
      return this.nonCBDSite && this.page.nonCBDIntroMedia
        ? this.page.nonCBDIntroMedia
        : this.page.introMedia
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.setPageColors({ backgroundColors: this.backgroundColors })
    })
  },
  methods: {
    ...mapMutations('screen', {
      setPageColors: 'setPageColors'
    }),

    getSectionBackgroundGradient(section) {
      if (section.colors && section.colors.length) {
        const gradientColors = [...section.colors].map(({ hex }) => hex)
        if (gradientColors.length === 1) {
          // if there is only one color, add it again to make it work as a gradient
          gradientColors.push(gradientColors[0])
        }
        return `linear-gradient(${gradientColors.join(', ')})`
      }
    },

    getSectionBackgroundImages(section) {
      return section.productCategories[0].productCategory.backgroundImages ?? []
    },

    getSectionButtonBackground(section) {
      if (section.colors && section.colors.length) {
        const colorMiddle =
          section.colors[Math.ceil(section.colors.length / 2) - 1]

        return Color(colorMiddle.hex)
          .alpha(0.5)
          .toString()
      }
    },

    getSectionStyle(section) {
      return {
        '--section-background': [
          this.getSectionBackgroundGradient(section)
        ].join(', ')
      }
    },

    getSectionCellBackgroundColor(section) {
      if (section.colors && section.colors.length) {
        const middleColor =
          section.colors[Math.floor(section.colors.length / 2)]
        const alpha = 1
        return Color(middleColor.hex)
          .alpha(alpha)
          .string()
      }
    },

    getSectionCloudsDisabled(section) {
      return this.getSectionBackgroundImages(section).length > 0
    }
  }
}
</script>

<style scoped>
.collection-page {
  padding-bottom: 0;
}

.collection-page__header-video {
  margin: 0 auto;
  max-width: 50%;
}

.collection-page__section {
  background-image: var(--section-background);
  position: relative;
}

.collection-page__section-area {
  border-top: 1px solid var(--text-color);
  position: relative;
}

@screen md {
  .collection-page__section-area {
    border-top: none;
  }
}

.collection-page__section:last-child {
  padding-bottom: 10em;
}

.collection-page__section-header {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  text-align: left;
}

@screen md {
  .collection-page__section-header {
    grid-template-columns: 50% 50%;
    grid-template-rows: auto;
  }
}

.collection-page__section >>> .sky {
  transform: translateZ(0);
}
</style>
